import React, { useContext } from "react";
import { motion } from "framer-motion";
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from "recharts";
import {
  Users,
  UserCheck,
  Baby,
  Home,
  Package,
  TrendingUp,
  Calendar,
} from "lucide-react";
import UsersContext from "../Context/UsersContext";
import moment from "moment";
import InvestmentWidgets from "../Components/InvestmentWidgets";

function Statistic() {
  const { farmers, users } = useContext(UsersContext);

  // number of VANO
  const numberOfVANOs = users.filter(
    (user) => user?.role === "fieldOfficer"
  ).length;

  // Gender distribution
  const maleFarmers = farmers.filter((farmer) => farmer?.gender === "male");
  const femaleFarmers = farmers.filter((farmer) => farmer?.gender === "female");

  const totalFarmers = farmers.length;

  // Calculate percentages
  const percentageMaleFarmers =
    totalFarmers > 0
      ? ((maleFarmers.length / totalFarmers) * 100).toFixed(2)
      : 0;

  const percentageFemaleFarmers =
    totalFarmers > 0
      ? ((femaleFarmers.length / totalFarmers) * 100).toFixed(2)
      : 0;

  // --- 1. Farmers in Groups ---
  const farmersInGroups = farmers.filter(
    (farmer) => farmer?.belongs_in_group === true
  );
  const farmersNotInGroups = farmers.filter(
    (farmer) => farmer?.belongs_in_group === false
  );

  const percentageInGroups =
    totalFarmers > 0
      ? ((farmersInGroups.length / totalFarmers) * 100).toFixed(2)
      : 0;

  const percentageNotInGroups =
    totalFarmers > 0
      ? ((farmersNotInGroups.length / totalFarmers) * 100).toFixed(2)
      : 0;

  // --- 2. New Farmers This Week and Month ---
  const now = moment();
  const startOfWeek = now.clone().startOf("isoWeek");
  const startOfMonth = now.clone().startOf("month");

  const newFarmersThisWeek = farmers.filter((farmer) =>
    moment(farmer?.created_at).isSameOrAfter(startOfWeek)
  );

  const newFarmersThisMonth = farmers.filter((farmer) =>
    moment(farmer?.created_at).isSameOrAfter(startOfMonth)
  );

  // household information
  let totalHouseholdSize = 0;
  let totalHouseholdsCount = 0;

  let totalChildrenUnder5 = 0;
  let householdsWithChildrenUnder5 = 0;

  let totalBreastfeedingChildren = 0;
  let householdsWithBreastfeedingChildren = 0;

  farmers?.forEach((farmer) => {
    // Average Household Size
    if (farmer?.number_of_people_in_house_hold) {
      totalHouseholdSize += farmer?.number_of_people_in_house_hold;
      totalHouseholdsCount += 1;
    }

    // Total Children Under 5 and households that have them
    if (farmer?.number_of_children_under_5) {
      totalChildrenUnder5 += farmer?.number_of_children_under_5;
      householdsWithChildrenUnder5 += 1;
    }

    // Total Breastfeeding Children and households that have them
    if (farmer?.number_of_breast_feeding_children) {
      totalBreastfeedingChildren += farmer?.number_of_breast_feeding_children;
      householdsWithBreastfeedingChildren += 1;
    }
  });

  // Final Calculations
  const averageHouseholdSize =
    totalHouseholdsCount > 0
      ? (totalHouseholdSize / totalHouseholdsCount).toFixed(2)
      : 0;

  const percentageHouseholdsWithChildrenUnder5 =
    totalHouseholdsCount > 0
      ? ((householdsWithChildrenUnder5 / totalHouseholdsCount) * 100).toFixed(2)
      : 0;

  const percentageHouseholdsWithBreastfeedingChildren =
    totalHouseholdsCount > 0
      ? (
          (householdsWithBreastfeedingChildren / totalHouseholdsCount) *
          100
        ).toFixed(2)
      : 0;

  // Initialize an empty object to store bundle counts
  const bundleCounts = {};

  //  Loop through farmers and count preferred bundles
  farmers.forEach((farmer) => {
    const bundle = farmer?.prefered_bundle?.trim();

    if (bundle) {
      bundleCounts[bundle] = (bundleCounts[bundle] || 0) + 1;
    }
  });

  //  Convert to array for sorting
  const sortedBundleCounts = Object.entries(bundleCounts).sort(
    (a, b) => b[1] - a[1]
  );

  // Chart data preparation
  const genderData = [
    { name: "Male", value: maleFarmers.length, color: "#10B981" },
    { name: "Female", value: femaleFarmers.length, color: "#F59E0B" },
  ];

  const groupData = [
    { name: "In Groups", value: farmersInGroups.length, color: "#10B981" },
    {
      name: "Not in Groups",
      value: farmersNotInGroups.length,
      color: "#F59E0B",
    },
  ];

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1 },
    },
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5 },
    },
  };

  // Stat Card Component
  const StatCard = ({
    title,
    value,
    subtitle,
    icon: Icon,
    color = "blue",
    delay = 0,
  }) => (
    <motion.div
      variants={cardVariants}
      initial="hidden"
      animate="visible"
      whileHover={{ scale: 1.02 }}
      className="p-6 bg-white border border-gray-200 shadow-lg dark:bg-gray-800 rounded-xl dark:border-gray-700"
    >
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
            {title}
          </p>
          <p className={`text-3xl font-bold text-gray-900 dark:text-gray-300`}>
            {value}
          </p>
          {subtitle && (
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              {subtitle}
            </p>
          )}
        </div>
        <div className={`p-3 rounded-full bg-${color}-100 dark:bg-gray-900`}>
          <Icon
            className={`w-6 h-6 text-${color}-600 dark:text-${color}-400`}
          />
        </div>
      </div>
    </motion.div>
  );

  // Chart Card Component
  const ChartCard = ({ title, children, className = "" }) => (
    <motion.div
      variants={cardVariants}
      initial="hidden"
      animate="visible"
      className={`bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6 border border-gray-200 dark:border-gray-700 ${className}`}
    >
      <h3 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
        {title}
      </h3>
      {children}
    </motion.div>
  );

  return (
    <div className="min-h-screen">
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mx-auto max-w-7xl"
      >
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            Overall Statistics and Analytics
          </h1>
          <p className="mt-2 text-gray-600 dark:text-gray-400">
            Comprehensive overview of farmer demographics and agriculture data
          </p>
        </div>

        {/* Stats Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="grid grid-cols-1 gap-6 mb-8 md:grid-cols-2 lg:grid-cols-4"
        >
          <StatCard
            title="Total Farmers"
            value={totalFarmers}
            icon={Users}
            color="green"
          />
          <StatCard
            title="Active village Agents"
            value={numberOfVANOs}
            icon={UserCheck}
            color="green"
          />
          <StatCard
            title="Enrolled This Week"
            value={newFarmersThisWeek.length}
            icon={TrendingUp}
            color="green"
          />
          <StatCard
            title="Enrolled This Month"
            value={newFarmersThisMonth.length}
            icon={Calendar}
            color="green"
          />
        </motion.div>

        {/* Charts Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="grid grid-cols-1 gap-6 mb-8 lg:grid-cols-2"
        >
          {/* Gender Distribution */}
          <ChartCard title="Gender Distribution">
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={genderData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) =>
                    `${name} ${(percent * 100).toFixed(0)}%`
                  }
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {genderData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
            <div className="grid grid-cols-2 gap-4 mt-4">
              <div className="text-center">
                <p className="text-sm text-gray-600 dark:text-gray-400">Male</p>
                <p className="text-xl font-semibold text-green-600">
                  {maleFarmers.length}
                </p>
                <p className="text-xs text-gray-500">
                  {percentageMaleFarmers}%
                </p>
              </div>
              <div className="text-center">
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Female
                </p>
                <p className="text-xl font-semibold text-yellow-600">
                  {femaleFarmers.length}
                </p>
                <p className="text-xs text-gray-500">
                  {percentageFemaleFarmers}%
                </p>
              </div>
            </div>
          </ChartCard>

          {/* Group Membership */}
          <ChartCard title="Group Membership">
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={groupData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) =>
                    `${name} ${(percent * 100).toFixed(0)}%`
                  }
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {groupData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
            <div className="grid grid-cols-2 gap-4 mt-4">
              <div className="text-center">
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  In Groups
                </p>
                <p className="text-xl font-semibold text-green-600">
                  {farmersInGroups.length}
                </p>
                <p className="text-xs text-gray-500">{percentageInGroups}%</p>
              </div>
              <div className="text-center">
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Not in Groups
                </p>
                <p className="text-xl font-semibold text-amber-600">
                  {farmersNotInGroups.length}
                </p>
                <p className="text-xs text-gray-500">
                  {percentageNotInGroups}%
                </p>
              </div>
            </div>
          </ChartCard>
        </motion.div>

        {/* Household Statistics */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="grid grid-cols-1 gap-6 mb-8 md:grid-cols-3"
        >
          <StatCard
            title="Average Household Size"
            value={averageHouseholdSize}
            icon={Home}
            color="green"
          />
          <StatCard
            title="Number of children under 5 years old"
            value={totalChildrenUnder5}
            subtitle={`${percentageHouseholdsWithChildrenUnder5}% of households`}
            icon={Baby}
            color="green"
          />
          <StatCard
            title="Breastfeeding Children"
            value={totalBreastfeedingChildren}
            subtitle={`${percentageHouseholdsWithBreastfeedingChildren}% of households`}
            icon={Baby}
            color="green"
          />
        </motion.div>

        {/* investment demographics */}
        <div className="mb-10">
          <InvestmentWidgets />
        </div>

        {/* Package Popularity Table */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="mt-8"
        >
          <ChartCard title="Most Purchased Package" className="w-full">
            <div className="overflow-x-auto">
              <div className="max-h-[300px] overflow-y-auto relative">
                <table className="w-full table-auto">
                  <thead className="sticky top-0 z-10 bg-white dark:bg-gray-800">
                    <tr className="border-b border-gray-200 dark:border-gray-700">
                      <th className="px-4 py-3 font-semibold text-left text-gray-900 dark:text-white">
                        Rank
                      </th>
                      <th className="px-4 py-3 font-semibold text-left text-gray-900 dark:text-white">
                        Package Name
                      </th>
                      <th className="px-4 py-3 font-semibold text-left text-gray-900 dark:text-white">
                        Farmers
                      </th>
                      <th className="px-4 py-3 font-semibold text-left text-gray-900 dark:text-white">
                        Popularity
                      </th>
                      <th className="px-4 py-3 font-semibold text-left text-gray-900 dark:text-white">
                        Progress
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedBundleCounts.map(([bundle, count], index) => {
                      const percentage =
                        totalFarmers > 0 ? (count / totalFarmers) * 100 : 0;
                      const maxCount = sortedBundleCounts[0]?.[1] || 1;
                      const progressWidth = (count / maxCount) * 100;

                      return (
                        <motion.tr
                          key={bundle}
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          className="transition-colors border-b border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700/50"
                        >
                          <td className="px-4 py-4">
                            <div className="flex items-center justify-center">
                              <span
                                className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold text-white ${
                                  index === 0
                                    ? "bg-gradient-to-br from-green-600 to-cyan-300 dark:bg-gray-900/50"
                                    : index === 1
                                    ? "bg-gradient-to-br from-green-600 to-cyan-300 dark:bg-gray-900/50"
                                    : index === 2
                                    ? "bg-gradient-to-br from-green-600 to-cyan-300 dark:bg-gray-900/50"
                                    : "bg-gradient-to-br from-green-600 to-cyan-300 dark:bg-gray-900/50"
                                }`}
                              >
                                {index + 1}
                              </span>
                            </div>
                          </td>
                          <td className="px-4 py-4">
                            <div className="font-medium text-gray-900 dark:text-white">
                              {bundle}
                            </div>
                          </td>
                          <td className="px-4 py-4">
                            <div className="font-semibold text-gray-700 dark:text-gray-300">
                              {count}
                            </div>
                          </td>
                          <td className="px-4 py-4">
                            <div className="text-gray-600 dark:text-gray-400">
                              {percentage.toFixed(1)}%
                            </div>
                          </td>
                          <td className="px-4 py-4">
                            <div className="flex items-center space-x-3">
                              <div className="flex-1 h-3 overflow-hidden bg-gray-200 rounded-full dark:bg-gray-700">
                                <motion.div
                                  initial={{ width: 0 }}
                                  animate={{ width: `${progressWidth}%` }}
                                  transition={{
                                    duration: 1,
                                    delay: index * 0.1,
                                  }}
                                  className={`h-full rounded-full ${
                                    index === 0
                                      ? "bg-gradient-to-r from-yellow-400 to-yellow-600"
                                      : index === 1
                                      ? "bg-gradient-to-r from-gray-400 to-gray-600"
                                      : index === 2
                                      ? "bg-gradient-to-r from-orange-400 to-orange-600"
                                      : "bg-gradient-to-r from-blue-400 to-blue-600"
                                  }`}
                                />
                              </div>
                              <span className="text-sm font-medium text-gray-600 dark:text-gray-400 min-w-[3rem]">
                                {Math.round(progressWidth)}%
                              </span>
                            </div>
                          </td>
                        </motion.tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {sortedBundleCounts.length === 0 && (
                <div className="py-8 text-center text-gray-500 dark:text-gray-400">
                  <Package className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>No package preferences recorded yet</p>
                </div>
              )}
            </div>

            {/* Summary Stats */}
            <div className="grid grid-cols-1 gap-4 pt-6 mt-6 border-t border-gray-200 md:grid-cols-3 dark:border-gray-700">
              <div className="text-center">
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Total Packages
                </p>
                <p className="text-2xl font-bold text-gray-900 dark:text-gray-400">
                  {sortedBundleCounts.length}
                </p>
              </div>
              <div className="text-center">
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Most Popular
                </p>
                <p className="text-2xl font-bold text-green-600 dark:text-green-400">
                  {sortedBundleCounts[0]?.[0] || "N/A"}
                </p>
              </div>
              <div className="text-center">
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Adoption Rate
                </p>
                <p className="text-2xl font-bold text-gray-900 dark:text-gray-400">
                  {sortedBundleCounts[0]
                    ? ((sortedBundleCounts[0][1] / totalFarmers) * 100).toFixed(
                        1
                      )
                    : 0}
                  %
                </p>
              </div>
            </div>
          </ChartCard>
        </motion.div>
      </motion.div>
    </div>
  );
}

export default Statistic;
